import { Alert, Card, CardContent, Checkbox, Divider, FormControlLabel, Grid, Typography } from '@mui/material';
import { CheckCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '../../../components/@extended/LoadingButton';
import useAuth from '../../../hooks/useAuth';
import { useAccountClient } from '../../../client/client-hook';
import { AutoMapSymbolsReply, AutoMapSymbolsReq } from '../../../proto/account-manager_pb';
import Loader from '../../../components/Loader';

type Props = {
  handleSuccess: () => void;
  suggestions: string[];
  masterAccount: string;
  slaveAccount: string;
  slaveId: string;
  overWriteExisting?: boolean;
  minWidth?: number;
};

const SymbolMismatchSelectionModal = ({
  handleSuccess,
  suggestions,
  masterAccount,
  slaveAccount,
  slaveId,
  overWriteExisting = false,
  minWidth = 0
}: Props) => {
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState(suggestions && suggestions.length > 0 ? suggestions[0] : '');
  // const [selectedType, setSelectedType] = useState('');
  const { user } = useAuth();
  const accountClient = useAccountClient();
  const [loading, setLoading] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(true);

  const onAutoMapping = () => {
    if (user && user?.tcId) {
      setLoading(true);
      accountClient()
        .then((client) => {
          const req: AutoMapSymbolsReq.AsObject = {
            userId: user?.tcId || '',
            slaveId: slaveId,
            masterAccount: masterAccount,
            slaveAccount: slaveAccount,
            pattern: selectedType,
            overwriteExisting: overWriteExisting ? confirmOverwrite : false
          };
          client
            .autoMapSymbols(req)
            .then((res) => {
              const result = res as AutoMapSymbolsReply.AsObject;
              console.log('AutoMapSymbolsReply: ', result);
              handleSuccess();
            })
            .catch((err) => {
              console.log('ERROR: ', err);
              setLoading(false);
            });
        })
        .catch((err) => {
          console.log('ERROR: ', err);
          setLoading(false);
        });
    }
  };

  const [maxCardHeight, setMaxCardHeight] = useState<any>('auto');

  useEffect(() => {
    // Calculate the maximum height among all cards
    const cards = document.querySelectorAll('.symbol-card'); // Adjust the selector to match your card elements
    let maxHeight = 0;

    cards.forEach((card) => {
      const cardHeight = card.clientHeight;
      if (cardHeight > maxHeight) {
        maxHeight = cardHeight;
      }
    });

    // Set the maximum height to all cards
    setMaxCardHeight(maxHeight);
  }, []);

  return (
    <>
      {loading && <Loader />}
      <Grid sx={{ px: 3, minWidth: minWidth ?? 1000 }}>
        <Grid
          sx={{
            maxHeight: '75vh',
            overflowY: 'scroll'
          }}
        >
          <Grid sx={{ mb: 2 }}>
            <Alert color="primary" icon={<InfoCircleFilled />}>
              We have detected a symbol mismatch on your Slave Forex pairs, you need to select which suffix we should use below. If you are
              unsure, please login to your trading platform and check which symbols allow trading, alternatively
              <span
                onClick={() => {
                  window.open('https://docs.tradersconnect.com/trade-copier/symbols/#symbol-mismatch-detected');
                }}
                style={{ cursor: 'pointer', color: theme.palette.primary.main }}
              >
                {' '}
                click here{' '}
              </span>
              to learn more.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Grid container rowSpacing={2} columnSpacing={4}>
              {suggestions.map((suggestion, index) => {
                return (
                  <>
                    <Grid item xs={suggestions?.length < 3 || suggestions.length === 4 ? 6 : 4} key={index + suggestion}>
                      <Card
                        className="symbol-card"
                        onClick={() => {
                          setSelectedType(suggestion);
                        }}
                        elevation={0}
                        sx={{
                          cursor: 'pointer',
                          border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f0f0f0'}`,
                          mt: 2,
                          height: maxCardHeight
                        }}
                      >
                        <CardContent sx={{ p: '30px !important' }}>
                          <Grid item xs={12}>
                            {suggestion?.toLowerCase() === 'example' && (
                              <>
                                <Grid display={'flex'} justifyContent={'center'}>
                                  <Typography variant={'h5'} fontSize={'1.3rem'} fontWeight={'bolder'}>
                                    {suggestion}
                                  </Typography>
                                </Grid>
                                <Grid xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                                  <Typography color={theme.palette.text.secondary}>
                                    In some cases your broker may allow you to trade without the suffix.
                                  </Typography>
                                  <br />
                                  <Typography color={theme.palette.text.secondary}>
                                    In 95% of cases, you will need to use the symbol with a suffix, therefore this option is not
                                    recommended.
                                  </Typography>
                                </Grid>
                              </>
                            )}
                            {suggestion.toLowerCase() !== 'example' && (
                              <>
                                <Grid display={'flex'} justifyContent={'center'}>
                                  <Typography variant={'h5'} fontSize={'1.3rem'} fontWeight={'bolder'}>
                                    {suggestion}
                                  </Typography>
                                </Grid>
                                <Grid xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                                  <Typography color={theme.palette.text.secondary}>
                                    We've detected your broker uses forex symbols with the suffix:{' '}
                                    {suggestion?.toLowerCase()?.replace('example', '')}
                                  </Typography>
                                  <br />
                                  <Typography color={theme.palette.text.secondary}>
                                    In situations where brokers offer multiple suffix options, it's highly likely you need to trade using
                                    the suffix symbols.
                                  </Typography>
                                </Grid>
                              </>
                            )}
                          </Grid>
                          <Grid sx={{ mt: 1.5 }} display={'flex'} justifyContent={'center'}>
                            {selectedType === suggestion && (
                              <Typography fontSize={20} color={'primary'} variant={'h5'} fontWeight={'bolder'}>
                                <CheckCircleFilled />
                              </Typography>
                            )}
                            {selectedType !== suggestion && (
                              <Grid
                                sx={{ height: '30px', marginY: 'auto', cursor: 'pointer' }}
                                display={'flex'}
                                justifyContent={'center'}
                                alignItems={'center'}
                                onClick={() => setSelectedType(suggestion)}
                              >
                                <Grid style={{ width: '20px', height: '20px', border: '2px solid #009688', borderRadius: '100%' }}></Grid>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    {/*{index < suggestions.length - 1 && <Grid item xs={1}></Grid>}*/}
                  </>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ width: '100%', ml: 0, mr: 0, mb: 2, mt: 2 }} />

        <Grid item xs={12} container justifyContent={overWriteExisting ? 'space-between' : 'end'}>
          {overWriteExisting && (
            <Grid>
              <FormControlLabel
                value="start"
                control={
                  <Checkbox
                    name="confirmOverwrite"
                    checked={confirmOverwrite}
                    onChange={(event, value) => {
                      console.log('confirmOverwrite: ', event, value);
                      setConfirmOverwrite(value);
                    }}
                  />
                }
                label="Overwrite existing forex mappings"
                labelPlacement="end"
                sx={{ mr: 1 }}
              />
            </Grid>
          )}
          <Grid item xs={6} display={'flex'} justifyContent="end" alignItems="flex-start">
            <div>
              <LoadingButton
                sx={{ paddingX: '24px' }}
                loadingPosition="end"
                endIcon={<></>}
                loading={loading}
                disabled={loading || !selectedType}
                color={loading || !selectedType ? 'secondary' : 'primary'}
                disableElevation
                fullWidth
                type="submit"
                variant="contained"
                // color={'primary'}
                onClick={() => {
                  onAutoMapping();
                }}
              >
                Continue
              </LoadingButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SymbolMismatchSelectionModal;
